// JobTray — 実行中/直近24hジョブの常駐トレイ (blueprint v2 Phase 1, ux_architect §3.3)
//   画面右下 CommandDock の左隣「ジョブ N」ボタン → 展開で直近24hジョブ一覧。
//   dead_letter レーンを最上部に分離表示 + [再試行](retryJob) / awaiting_approval に [承認](approveJob)。
//   poll間隔: 実行中(queued/claimed/running)あり=10秒 / 無し=60秒。
//   スタイルは JournalReviewPage パレット踏襲のインライン style (vibes新規使用禁止)。
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdChecklist, MdClose } from 'react-icons/md';
import {
  Job,
  JobStatus,
  fetchJobs,
  retryJob,
  approveJob,
  cancelJob,
} from '../api/client';
import { jobStatusToUnified } from '../lib/statusVocab';

// ---------------------------------------------------------------------------
// 共有パレット/ヘルパ (JournalReviewPage の C/FONT/NUM 移植 — CommandDock からも import)
// ---------------------------------------------------------------------------
export const JOBS_C = {
  ground: '#edf1f5', ink: '#1d2a3a', sub: '#647386',
  line: '#d9e0e7', lineDark: '#bdc8d3', navy: '#203a57',
  blueTint: '#f0f6ff', blueLine: '#b9d2ed',
  good: '#267a62', goodBg: '#edf8f4', warn: '#a46a12', warnBg: '#fdf3e3',
  danger: '#b3261e', dangerBg: '#fdecea',
};
export const JOBS_FONT = '"Hiragino Sans","Yu Gothic UI","Meiryo",-apple-system,"Segoe UI",sans-serif';
export const JOBS_NUM = '"SFMono-Regular",Consolas,"Liberation Mono",Menlo,monospace';

// 行動レイヤの語・色は統一語彙 (lib/statusVocab.ts jobStatusToUnified)。
//   failed/dead_letter は「再試行で前進 = あなたの番」= 橙バッジ (赤はレーン見出し=事実レイヤのみ)。
//   生の status は tooltip に退避。
export const JOB_STATUS_LABEL: Record<JobStatus, string> = {
  queued: '待機中',
  awaiting_approval: jobStatusToUnified('awaiting_approval').label,
  claimed: '受付済',
  running: '実行中',
  succeeded: '成功',
  failed: jobStatusToUnified('failed').label,
  cancelled: '取消',
  dead_letter: jobStatusToUnified('dead_letter').label,
};

const badgeColors = (s: JobStatus): { fg: string; bg: string; bd: string } => {
  switch (s) {
    case 'succeeded': return { fg: JOBS_C.good, bg: JOBS_C.goodBg, bd: '#9fdbc7' };
    case 'failed':
    case 'dead_letter':
    case 'awaiting_approval': {
      const u = jobStatusToUnified(s);
      return { fg: u.fg, bg: u.bg, bd: u.bd };
    }
    case 'claimed':
    case 'running': return { fg: '#234e79', bg: JOBS_C.blueTint, bd: JOBS_C.blueLine };
    case 'cancelled': return { fg: '#5e7083', bg: '#f1f4f7', bd: '#ccd6df' };
    case 'queued':
    default: return { fg: JOBS_C.sub, bg: '#f4f7fa', bd: JOBS_C.lineDark };
  }
};

export const JobStatusBadge: React.FC<{ status: JobStatus }> = ({ status }) => {
  const c = badgeColors(status);
  return (
    <span title={`status: ${status}`} style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 7px', borderRadius: 3,
      fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap',
      color: c.fg, background: c.bg, border: `1px solid ${c.bd}`,
    }}>
      {JOB_STATUS_LABEL[status] || status}
    </span>
  );
};

/** epoch ms → 「たった今 / N分 / N時間 / N日」 */
export const fmtElapsed = (fromMs: number, nowMs: number = Date.now()): string => {
  const d = Math.max(0, nowMs - fromMs);
  if (d < 60_000) return 'たった今';
  if (d < 3_600_000) return `${Math.floor(d / 60_000)}分前`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}時間前`;
  return `${Math.floor(d / 86_400_000)}日前`;
};

/** error_json → 1行表示文字列 (無ければ null) */
export const jobErrorLine = (j: Job): string | null => {
  if (!j.error_json) return null;
  try {
    const e = JSON.parse(j.error_json);
    const s = e.message || e.detail || e.code || e.class || j.error_json;
    return String(s).slice(0, 120);
  } catch {
    return String(j.error_json).slice(0, 120);
  }
};

/** result_json → 表示テキスト抽出。
 *  runner は claude CLI の応答封筒 {type:'result', result:'<回答本文>'} をそのまま格納するため、
 *  .result を最優先で剥がす (2026-07-07 「成功したのに何も帰ってこない」修正)。
 *  claude が構造化JSON({status,summary,details})を返した場合は入れ子も解決する。 */
export const jobResultLine = (j: Job): string | null => {
  if (!j.result_json) return null;
  try {
    const r = JSON.parse(j.result_json);
    // claude CLI封筒: result フィールドに最終回答テキスト (または構造化JSON文字列)
    let inner: any = r.result !== undefined ? r.result : r;
    if (typeof inner === 'string') {
      const s0 = inner.trim();
      // 「本文 + 契約JSON」混在出力(claudeが両方書くことがある)→ JSON部のsummaryを優先、無ければ本文のみ
      const jsonAt = s0.search(/\{\s*"(status|summary)"/);
      if (jsonAt > 0) {
        try {
          const tail = JSON.parse(s0.slice(jsonAt));
          if (tail && tail.summary) return String(tail.summary).slice(0, 500);
        } catch { /* JSON部が壊れていれば本文へ */ }
        return s0.slice(0, jsonAt).trim().slice(0, 500) || null;
      }
      if (s0.startsWith('{')) {
        try { inner = JSON.parse(s0); } catch { return s0.slice(0, 500) || null; }
      } else {
        return s0.slice(0, 500) || null;
      }
    }
    if (inner && typeof inner === 'object') {
      const s = inner.summary || inner.message || inner.result || null;
      if (s) return String(s).slice(0, 500);
      if (inner.details) return String(typeof inner.details === 'string' ? inner.details : JSON.stringify(inner.details)).slice(0, 500);
    }
    const s = r.summary || r.message || null;
    return s ? String(s).slice(0, 500) : null;
  } catch {
    return null;
  }
};

export const isActiveJobStatus = (s: JobStatus): boolean =>
  s === 'queued' || s === 'claimed' || s === 'running';

// ---------------------------------------------------------------------------
// ライブ行 — 実行中ジョブの「今何をしているか」(worker progress_note, 2026-07-08)
//   queued: 「順番待ち(前にN件)」(概算不能なら「順番待ち…」)
//   claimed/running: 「⏳ {progress_note}」/ null の間は「処理を開始しています…」
//   10秒pollで progress_note が変われば自然に文言が更新される。
// ---------------------------------------------------------------------------
const LIVE_PULSE_CSS =
  '@keyframes freeemanLivePulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.3;transform:scale(.7)}}';
let livePulseInjected = false;
const ensureLivePulseCss = () => {
  if (livePulseInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.setAttribute('data-freeeman', 'live-pulse');
  el.textContent = LIVE_PULSE_CSS;
  document.head.appendChild(el);
  livePulseInjected = true;
};

/** ライブ行テキスト。queueAhead=null は前方件数の概算不能 */
export const jobLiveText = (j: Job, queueAhead: number | null): string => {
  if (j.status === 'queued') {
    if (queueAhead == null) return '順番待ち…';
    return queueAhead > 0 ? `順番待ち(前に${queueAhead}件)` : '順番待ち(まもなく開始します)';
  }
  return (j.progress_note && j.progress_note.trim()) || '処理を開始しています…';
};

const liveWarnChip: React.CSSProperties = {
  flex: '0 0 auto', padding: '1px 6px', borderRadius: 3,
  color: JOBS_C.warn, background: JOBS_C.warnBg, border: '1px solid #e8c98a',
  fontSize: 10, fontWeight: 700,
};

export const JobLiveLine: React.FC<{
  job: Job;
  nowMs: number;
  /** queued 時の前方件数概算 (null=概算不能) */
  queueAhead?: number | null;
  /** JobTray一覧では経過時間 + 長時間注記も表示 */
  showElapsed?: boolean;
}> = ({ job, nowMs, queueAhead = null, showElapsed = false }) => {
  ensureLivePulseCss();
  const queued = job.status === 'queued';
  const text = jobLiveText(job, queueAhead);
  const elapsedMs = Math.max(0, nowMs - job.created_at);
  const min = Math.floor(elapsedMs / 60_000);
  // 長時間注記は実行系 (claimed/running) のみ — queued の滞留はタイムアウトと無関係
  const slow = !queued && showElapsed && elapsedMs > 5 * 60_000;
  const nearTimeout = !queued && showElapsed && elapsedMs > 15 * 60_000;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexWrap: 'wrap' }}>
      <span
        aria-hidden
        style={{
          flex: '0 0 auto', width: 7, height: 7, borderRadius: '50%',
          background: queued ? '#93a3b3' : '#2f6fb2',
          animation: 'freeemanLivePulse 1.4s ease-in-out infinite',
        }}
      />
      <span
        title={text}
        style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontStyle: 'italic', color: JOBS_C.sub, fontSize: 11, fontWeight: 600,
        }}
      >
        {'⏳ '}{text}
      </span>
      {showElapsed && (
        <span style={{
          flex: '0 0 auto', color: JOBS_C.sub, fontFamily: JOBS_NUM,
          fontSize: 10, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
        }}>
          ({min >= 1 ? `${min}分経過` : '1分未満'})
        </span>
      )}
      {nearTimeout ? (
        <span style={{ ...liveWarnChip, fontWeight: 800 }}>まもなくタイムアウトします</span>
      ) : slow ? (
        <span style={liveWarnChip}>長引いています</span>
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// styles
// ---------------------------------------------------------------------------
const S = {
  trayBtn: {
    position: 'fixed', right: 176, bottom: 16, zIndex: 80,
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '10px 14px', borderRadius: 22, cursor: 'pointer',
    border: `1px solid ${JOBS_C.lineDark}`, background: '#fff', color: JOBS_C.navy,
    font: `800 13px ${JOBS_FONT}`, boxShadow: '0 6px 18px rgba(33,52,74,.18)',
  } as React.CSSProperties,
  countNum: { fontFamily: JOBS_NUM, fontVariantNumeric: 'tabular-nums' } as React.CSSProperties,
  deadDot: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
    background: JOBS_C.danger, color: '#fff', fontFamily: JOBS_NUM, fontSize: 10, fontWeight: 800,
  } as React.CSSProperties,
  panel: {
    position: 'fixed', right: 16, bottom: 64, zIndex: 85,
    width: 440, maxWidth: 'calc(100vw - 32px)', maxHeight: '68vh',
    display: 'flex', flexDirection: 'column',
    border: `1px solid ${JOBS_C.lineDark}`, borderRadius: 10, background: '#fff',
    boxShadow: '0 12px 30px rgba(33,52,74,.24)', overflow: 'hidden',
    fontFamily: JOBS_FONT, color: JOBS_C.ink,
  } as React.CSSProperties,
  panelHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
    padding: '10px 14px', background: JOBS_C.navy, color: '#fff',
  } as React.CSSProperties,
  panelTitle: { margin: 0, fontSize: 14, fontWeight: 800, letterSpacing: '.03em' } as React.CSSProperties,
  closeBtn: {
    display: 'grid', placeItems: 'center', width: 26, height: 26, borderRadius: 4,
    border: '1px solid rgba(255,255,255,.35)', background: 'rgba(255,255,255,.1)',
    color: '#e5eef8', cursor: 'pointer',
  } as React.CSSProperties,
  body: { overflowY: 'auto', padding: '10px 12px 12px', background: '#f7f9fb' } as React.CSSProperties,
  laneHead: (danger: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 6, margin: '4px 2px 6px',
    color: danger ? JOBS_C.danger : JOBS_C.sub, fontSize: 10, letterSpacing: '.08em', fontWeight: 800,
  }),
  row: (danger: boolean): React.CSSProperties => ({
    display: 'flex', flexDirection: 'column', gap: 5,
    padding: '8px 10px', marginBottom: 6, borderRadius: 6,
    border: `1px solid ${danger ? '#e6a49e' : JOBS_C.line}`,
    background: danger ? JOBS_C.dangerBg : '#fff',
  }),
  rowTop: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 } as React.CSSProperties,
  jobType: {
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    fontSize: 12.5, fontWeight: 800, color: '#22364b',
  } as React.CSSProperties,
  jobId: {
    flex: '0 0 auto', padding: '2px 5px', border: '1px solid #d7e2ef', borderRadius: 3,
    color: '#476b8e', background: '#f3f8fd', fontFamily: JOBS_NUM, fontSize: 9.5, fontWeight: 800,
  } as React.CSSProperties,
  elapsed: {
    marginLeft: 'auto', flex: '0 0 auto', color: JOBS_C.sub,
    fontFamily: JOBS_NUM, fontSize: 10.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,
  errLine: {
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    color: JOBS_C.danger, fontSize: 11, fontWeight: 600,
  } as React.CSSProperties,
  actRow: { display: 'flex', alignItems: 'center', gap: 6 } as React.CSSProperties,
  actBtn: (kind: 'good' | 'danger' | 'plain'): React.CSSProperties => ({
    padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
    font: `800 11px ${JOBS_FONT}`,
    color: kind === 'good' ? '#fff' : kind === 'danger' ? JOBS_C.danger : '#53657a',
    background: kind === 'good' ? JOBS_C.good : '#fff',
    border: `1px solid ${kind === 'good' ? JOBS_C.good : kind === 'danger' ? '#e6a49e' : JOBS_C.lineDark}`,
  }),
  empty: { padding: '18px 8px', textAlign: 'center', color: JOBS_C.sub, fontSize: 12, fontWeight: 600 } as React.CSSProperties,
  loadErr: { padding: '8px 10px', color: JOBS_C.danger, fontSize: 11, fontWeight: 700 } as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// component
// ---------------------------------------------------------------------------
export const JobTray: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null); // job id が操作中
  const [now, setNow] = useState<number>(Date.now());
  const [counts, setCounts] = useState<Record<string, number>>({});
  const aliveRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    let merged: Job[] = [];
    try {
      // 直近リスト + dead_letter (24hより古い dead_letter も対応待ちなので別クエリで拾う)
      const [recent, dead] = await Promise.all([
        fetchJobs({ limit: 100 }),
        fetchJobs({ status: 'dead_letter', limit: 50 }),
      ]);
      if (recent.counts) setCounts(recent.counts); // LIMIT非依存の実数 (2026-07-07)
      const byId = new Map<string, Job>();
      [...(recent.jobs || []), ...(dead.jobs || [])].forEach((j) => byId.set(j.id, j));
      const cutoff = Date.now() - 24 * 3600_000;
      merged = Array.from(byId.values())
        .filter((j) => j.status === 'dead_letter' || j.created_at >= cutoff)
        .sort((a, b) => b.created_at - a.created_at);
      if (!aliveRef.current) return;
      setJobs(merged);
      setLoadError(null);
      setNow(Date.now());
    } catch (e: any) {
      if (!aliveRef.current) return;
      setLoadError(e?.message || 'ジョブ一覧の取得に失敗しました');
    }
    if (!aliveRef.current) return;
    // 実行中があれば10秒 / 無ければ60秒で次poll
    const hasActive = merged.some((j) => isActiveJobStatus(j.status));
    timerRef.current = setTimeout(load, hasActive ? 10_000 : 60_000);
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    load();
    return () => {
      aliveRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [load]);

  const act = async (id: string, fn: (id: string) => Promise<any>) => {
    if (acting) return;
    setActing(id);
    try {
      await fn(id);
    } catch (e: any) {
      setLoadError(e?.message || '操作に失敗しました');
    } finally {
      setActing(null);
      load(); // 即時反映
    }
  };

  const deadJobs = jobs.filter((j) => j.status === 'dead_letter');
  const normalJobs = jobs.filter((j) => j.status !== 'dead_letter');
  // 実数カウント(LIMITで切れた表示件数でなく全件) — 173件run等での「100件しかない」誤解の根絶
  const activeCount = Object.keys(counts).length
    ? (counts['queued'] || 0) + (counts['claimed'] || 0) + (counts['running'] || 0) + (counts['awaiting_approval'] || 0)
    : jobs.filter((j) => isActiveJobStatus(j.status) || j.status === 'awaiting_approval').length;

  // queued の前方件数概算: 実行中(claimed/running)の実数(counts優先) + 表示リスト内の先行 queued
  const queueAheadOf = (j: Job): number => {
    const inFlight = Object.keys(counts).length
      ? (counts['claimed'] || 0) + (counts['running'] || 0)
      : jobs.filter((x) => x.status === 'claimed' || x.status === 'running').length;
    const earlierQueued = jobs.filter(
      (x) => x.id !== j.id && x.status === 'queued'
        && (x.created_at < j.created_at || (x.created_at === j.created_at && x.id < j.id)),
    ).length;
    return inFlight + earlierQueued;
  };

  const renderRow = (j: Job, danger: boolean) => {
    const err = jobErrorLine(j);
    return (
      <div key={j.id} style={S.row(danger)}>
        <div style={S.rowTop}>
          <span style={S.jobId}>{j.id}</span>
          <span style={S.jobType} title={j.type}>{j.type}</span>
          <JobStatusBadge status={j.status} />
          <span style={S.elapsed}>{fmtElapsed(j.created_at, now)}</span>
        </div>
        {isActiveJobStatus(j.status) && (
          <JobLiveLine
            job={j}
            nowMs={now}
            queueAhead={j.status === 'queued' ? queueAheadOf(j) : null}
            showElapsed
          />
        )}
        {err && <div style={S.errLine} title={err}>{err}</div>}
        {(j.status === 'dead_letter' || j.status === 'failed' || j.status === 'awaiting_approval') && (
          <div style={S.actRow}>
            {(j.status === 'dead_letter' || j.status === 'failed') && (
              <button
                type="button"
                style={S.actBtn('plain')}
                disabled={acting === j.id}
                onClick={() => act(j.id, retryJob)}
              >
                再試行
              </button>
            )}
            {j.status === 'awaiting_approval' && (
              <>
                <button
                  type="button"
                  style={S.actBtn('good')}
                  disabled={acting === j.id}
                  onClick={() => act(j.id, (id) => approveJob(id))}
                >
                  承認
                </button>
                <button
                  type="button"
                  style={S.actBtn('danger')}
                  disabled={acting === j.id}
                  onClick={() => act(j.id, (id) => cancelJob(id))}
                >
                  取消
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        style={S.trayBtn}
        aria-expanded={open}
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        title="直近24hのジョブ一覧"
      >
        <MdChecklist size={16} aria-hidden />
        <span>ジョブ <span style={S.countNum}>{activeCount}</span></span>
        {deadJobs.length > 0 && <span style={S.deadDot}>{deadJobs.length}</span>}
      </button>
      {open && (
        <div style={S.panel} role="dialog" aria-label="ジョブ一覧">
          <div style={S.panelHead}>
            <h3 style={S.panelTitle}>ジョブ(直近24h)</h3>
            <button type="button" style={S.closeBtn} aria-label="閉じる" onClick={() => setOpen(false)}>
              <MdClose size={15} />
            </button>
          </div>
          <div style={S.body}>
            {loadError && <div style={S.loadErr}>{loadError}</div>}
            {deadJobs.length > 0 && (
              <>
                <div style={S.laneHead(true)}>処理失敗 — 対応待ち {deadJobs.length}件 (dead_letter・再試行で前進)</div>
                {deadJobs.map((j) => renderRow(j, true))}
              </>
            )}
            <div style={S.laneHead(false)}>直近24H({normalJobs.length}件)</div>
            {normalJobs.length === 0 && !loadError && (
              <div style={S.empty}>直近24時間のジョブはありません</div>
            )}
            {normalJobs.map((j) => renderRow(j, false))}
          </div>
        </div>
      )}
    </>
  );
};

export default JobTray;
